import type { DateTimeConfig, DateTimeTypeConfig } from "@/models/custom-fields";

export type DateTimeFormat = DateTimeConfig["format"];

const OFFSET_PATTERN = /^([+-])([01]\d|2[0-3]):([0-5]\d)$/;

export const defaultDateTimeConfig = (): DateTimeConfig => ({
  format: "date-time",
  timezone: "utc",
  defaultNow: false,
});

export const hasDate = (format: DateTimeFormat): boolean => format !== "time";
export const hasTime = (format: DateTimeFormat): boolean => format !== "date";

/** The format that stores the given parts; at least one of the two is always kept. */
export function formatFor(date: boolean, time: boolean): DateTimeFormat {
  if (date && time) return "date-time";
  return time ? "time" : "date";
}

export type TimezoneKind = "utc" | "local" | "offset" | "zone";

export function timezoneKind(timezone: string): TimezoneKind {
  if (timezone === "utc") return "utc";
  if (timezone === "local") return "local";
  return OFFSET_PATTERN.test(timezone) ? "offset" : "zone";
}

export function isValidZone(zone: string): boolean {
  if (!zone) return false;
  try {
    new Intl.DateTimeFormat("en", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** `utc`, `local`, `+02:00` or a zone name the browser knows. */
export function isValidTimezone(timezone: string): boolean {
  const kind = timezoneKind(timezone);
  return kind === "zone" ? isValidZone(timezone) : true;
}

export function formatOffset(minutes: number): string {
  const sign = minutes < 0 ? "-" : "+";
  const absolute = Math.abs(minutes);
  return `${sign}${String(Math.floor(absolute / 60)).padStart(2, "0")}:${String(absolute % 60).padStart(2, "0")}`;
}

export function parseOffset(offset: string): number | null {
  const match = OFFSET_PATTERN.exec(offset);
  if (!match) return null;
  const minutes = Number(match[2]) * 60 + Number(match[3]);
  return match[1] === "-" ? -minutes : minutes;
}

type WallClock = { year: number; month: number; day: number; hour: number; minute: number; second: number };

function wallClockIn(zone: string, instant: Date): WallClock {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(instant);
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return {
    year: part("year"),
    month: part("month"),
    day: part("day"),
    hour: part("hour"),
    minute: part("minute"),
    second: part("second"),
  };
}

const asUtcMs = (w: WallClock): number => Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);

/** Offset of an IANA zone from UTC at an instant, in minutes. */
export function zoneOffsetMinutes(zone: string, instant: Date): number {
  const wall = asUtcMs(wallClockIn(zone, instant));
  return Math.round((wall - Math.floor(instant.getTime() / 1000) * 1000) / 60000);
}

/** The offset a wall clock reading has in a zone; around a DST change the later reading wins. */
export function zoneOffsetForWallClock(zone: string, wall: WallClock): number {
  const guess = asUtcMs(wall);
  const first = zoneOffsetMinutes(zone, new Date(guess));
  const second = zoneOffsetMinutes(zone, new Date(guess - first * 60000));
  return second;
}

/** Offset in minutes for a wall clock in the configured timezone, `null` for `local` (no offset is stored). */
function offsetFor(timezone: string, wall: WallClock): number | null {
  switch (timezoneKind(timezone)) {
    case "utc":
      return 0;
    case "local":
      return null;
    case "offset":
      return parseOffset(timezone);
    default:
      return zoneOffsetForWallClock(timezone, wall);
  }
}

const two = (n: number) => String(n).padStart(2, "0");

function render(wall: WallClock, config: DateTimeConfig): string {
  const date = `${String(wall.year).padStart(4, "0")}-${two(wall.month)}-${two(wall.day)}`;
  if (config.format === "date") return date;
  const offset = offsetFor(config.timezone, wall);
  const suffix = offset === null ? "" : config.timezone === "utc" ? "Z" : formatOffset(offset);
  const time = `${two(wall.hour)}:${two(wall.minute)}:${two(wall.second)}${suffix}`;
  return config.format === "time" ? time : `${date}T${time}`;
}

/** What "now" is as a stored value: the wall clock of the configured timezone, with its offset. */
export function nowFor(config: DateTimeConfig, now: Date = new Date()): string {
  const timezone = config.timezone;
  const kind = timezoneKind(timezone);
  let wall: WallClock;
  if (kind === "local") {
    wall = {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
      hour: now.getHours(),
      minute: now.getMinutes(),
      second: now.getSeconds(),
    };
  } else if (kind === "utc") {
    wall = wallClockIn("UTC", now);
  } else if (kind === "offset") {
    wall = wallClockIn("UTC", new Date(now.getTime() + (parseOffset(timezone) ?? 0) * 60000));
  } else {
    wall = wallClockIn(timezone, now);
  }
  return render(wall, config);
}

/** Splits a stored value into its wall clock parts, ignoring the offset; `null` if it is not a value of the format. */
export function parseWallClock(value: unknown, format: DateTimeFormat): WallClock | null {
  if (typeof value !== "string") return null;
  const date = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  const time = /(?:^|T)(\d{2}):(\d{2})(?::(\d{2}))?/.exec(value);
  if (hasDate(format) && !date) return null;
  if (hasTime(format) && !time) return null;
  return {
    year: date ? Number(date[1]) : 1970,
    month: date ? Number(date[2]) : 1,
    day: date ? Number(date[3]) : 1,
    hour: time ? Number(time[1]) : 0,
    minute: time ? Number(time[2]) : 0,
    second: time ? Number(time[3] ?? 0) : 0,
  };
}

/**
 * The value for `ion-datetime`: Ionic shows the wall clock parts of the string and ignores any offset, which is
 * exactly what we want, since the picker is read in the configured timezone.
 */
export function toPickerValue(value: unknown, config: DateTimeConfig): string | undefined {
  const wall = parseWallClock(value, config.format);
  if (!wall) return undefined;
  const date = `${String(wall.year).padStart(4, "0")}-${two(wall.month)}-${two(wall.day)}`;
  const time = `${two(wall.hour)}:${two(wall.minute)}:${two(wall.second)}`;
  if (config.format === "date") return date;
  return config.format === "time" ? time : `${date}T${time}`;
}

/** The stored value for what the picker emitted (wall clock parts in the configured timezone). */
export function fromPickerValue(picked: unknown, config: DateTimeConfig): string | null {
  const wall = parseWallClock(typeof picked === "string" ? picked.replace(/(Z|[+-]\d{2}:\d{2})$/, "") : picked, config.format);
  return wall ? render(wall, config) : null;
}

const TIMEZONE_LABEL = (timezone: string): string => {
  switch (timezoneKind(timezone)) {
    case "utc":
      return " UTC";
    case "local":
      return "";
    case "offset":
      return ` UTC${timezone}`;
    default:
      return ` (${timezone})`;
  }
};

/** Human readable value: the wall clock the user entered in the user's locale, with the timezone it is in. */
export function displayValue(value: unknown, config: DateTimeConfig, locale?: string): string {
  const wall = parseWallClock(value, config.format);
  if (!wall) return typeof value === "string" ? value : "";
  const moment = new Date(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second);
  if (config.format === "date") {
    return moment.toLocaleDateString(locale, { dateStyle: "medium" });
  }
  if (config.format === "time") {
    return moment.toLocaleTimeString(locale, { timeStyle: "medium" }) + TIMEZONE_LABEL(config.timezone);
  }
  return moment.toLocaleString(locale, { dateStyle: "medium", timeStyle: "medium" }) + TIMEZONE_LABEL(config.timezone);
}

/** The settings of a `datetime` field out of its stored `type_config`, with the defaults the backend fills in. */
export function resolveDateTimeConfig(typeConfig: unknown): DateTimeConfig {
  const properties = ((typeConfig && typeof typeConfig === "object" ? typeConfig : {}) as Partial<DateTimeTypeConfig>).properties;
  const defaults = defaultDateTimeConfig();
  return {
    format: properties?.value?.format ?? defaults.format,
    timezone: properties?.timezone?.const ?? defaults.timezone,
    defaultNow: properties?.default_now?.const ?? defaults.defaultNow,
  };
}

/** The `type_config` to send for the settings: the narrowing fragment of the OpenInv definition, always complete. */
export function toDateTimeTypeConfig(config: DateTimeConfig): DateTimeTypeConfig {
  return {
    properties: {
      value: { format: config.format },
      timezone: { const: config.timezone },
      default_now: { const: config.defaultNow },
    },
  };
}
